/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useCallback } from 'react';
import { css, jsx } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Typography as Text } from '@mui/material';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

import { forgotPassword } from '../actions/UserActions';
import Skeleton from '../components/Skeleton';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleReset = useCallback(async () => {
    try {
      setError(null);
      await forgotPassword(email);
      setSuccess(true);
    } catch (e) {
      if (e?.response?.status === 429) {
        setError(
          t(
            'Du har skickat för många återställninglänkar, vänligen försök igen om en stund'
          )
        );
        return;
      }
      setError(
        t(
          'Vi kunde inte skapa en återställningslänk. Kontrollera att du har skrivit in rätt e-postadress.'
        )
      );
    }
  }, [email, t]);

  return (
    <Skeleton title={t('Klang.ai | Glömt lösenord')} publicPage>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          max-width: 450px;
          width: 100%;
          margin: 0 auto;
        `}
      >
        {!success && (
          <React.Fragment>
            <Text variant="h4" fontWeight="700">
              {t('Återställ lösenord')}
            </Text>
            <Text
              css={css`
                margin-top: 1rem;
              `}
              variant="body1"
            >
              {t(
                'Skriv in din e-postadress så skickar vi ett mail med instruktioner om hur du återställer ditt lösenord.'
              )}
            </Text>
            {error && (
              <Text
                css={css`
                  margin-top: 1rem;
                `}
                variant="body1"
                color="error"
              >
                {error}
              </Text>
            )}
            <TextField
              css={css`
                margin-top: 1rem;
              `}
              id="email"
              label={t('E-postadress')}
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
            <Button
              css={css`
                margin-top: 1rem;
              `}
              variant="contained"
              color="primary"
              onClick={handleReset}
              fullWidth
            >
              {t('Återställ lösenord')}
            </Button>
          </React.Fragment>
        )}
        {success && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
            `}
          >
            <Text variant="h4" fontWeight="700">
              {t('Återställningslänk skickad')}
            </Text>
            <Text
              css={css`
                margin-top: 1rem;
                text-align: center;
              `}
              variant="body1"
            >
              {t(
                'Vi har skickat en återställningslänk till din e-postadress. Klicka på länken för att återställa ditt lösenord.'
              )}
            </Text>
            <CheckCircleOutlineOutlinedIcon
              css={css`
                margin-top: 1rem;
                font-size: 3rem;
              `}
              color="primary"
            />
          </div>
        )}
      </div>
    </Skeleton>
  );
}
